import { Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import { correctionsApi } from '../../api/corrections'
import { RefusedError } from '../../api/client'
import { CorrectionThread } from '../../components/Correct'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { factsOf, kindLabel, ruleSentence, STATUS_TONE, STATUS_WORDS } from '../../lib/improve'
import { Verification } from './Verification'

// One correction, opened: what was said and what it should have been, what Scout found when it re-read the
// evidence, the discussion, every candidate rule with its verdicts, and the lesson — positive or negative.
// A correction the loop could not generalise says so at the top; it is not dressed as a pending success.

export function CorrectionDrawer({ correctionId, onClose }: { correctionId: string; onClose: () => void }) {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['correction', spoke, correctionId], queryFn: () => correctionsApi.detail(spoke, correctionId) })
  const d = q.data

  return (
    <aside className="deal-drawer improve-drawer" aria-label="Correction">
      <div className="actions space-between">
        <div>
          <p className="kicker">{d ? kindLabel(d.correction.entityKind) : 'Correction'}</p>
          <h2>{d?.correction.entityLabel ?? d?.correction.entityId ?? 'Correction'}</h2>
        </div>
        <button className="button small" onClick={onClose}>Close</button>
      </div>

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p></div>}

      {d && (
        <>
          <p className={`drawer-status tone-${STATUS_TONE[d.correction.status]}`}>
            <span className={`tag sig ${STATUS_TONE[d.correction.status]}`}>{STATUS_WORDS[d.correction.status]}</span>
            {d.correction.status === 'needs-human' && ' Nothing general could be proved from this one. It needs a rule stated by hand, or a different correction.'}
            {d.correction.status === 'open' && ' Tonight’s run has not read it yet.'}
            {d.correction.status === 'improved' && ' A rule came out of this, and it holds against everything already on file.'}
            {d.correction.status === 'declined' && ' Every candidate failed its checks. The reasons are below.'}
          </p>

          <dl className="facts compact">
            <dt>Scout said</dt><dd>{d.correction.fromValue ?? <span className="muted">nothing yet</span>}</dd>
            <dt>You said</dt><dd><strong>{d.correction.toValue}</strong></dd>
            <dt>Because</dt><dd>{d.correction.reason}</dd>
            <dt>Recorded</dt><dd>{formatDate(d.correction.at)}</dd>
          </dl>

          {d.correction.investigation && (
            <details className="investigation" open>
              <summary>What Scout found when it re-read the evidence</summary>
              <p>{d.correction.investigation}</p>
            </details>
          )}

          {d.correction.hypothesis && (
            <div className="hypothesis">
              <p className="kicker">What the discussion concluded</p>
              <dl className="facts compact">
                {factsOf(d.correction.hypothesis).map((f) => (
                  <Fragment key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></Fragment>
                ))}
              </dl>
            </div>
          )}

          <h3>The discussion</h3>
          <CorrectionThread correctionId={correctionId} compact />

          <h3>What the loop proposed</h3>
          {d.proposals.length === 0 ? (
            <p className="muted small">
              {d.correction.status === 'open'
                ? 'Nothing yet — the run reads this tonight.'
                : 'No candidate rule was written for this one.'}
            </p>
          ) : (
            d.proposals.map((p) => <Verification key={p.proposalId} proposal={p} />)
          )}

          {d.rule && (
            <>
              <h3>The rule that is now live</h3>
              <div className="rule-inline">
                <p>{ruleSentence(d.rule.kind, d.rule.definition) ?? `A ${d.rule.kind} rule.`}</p>
                <p className="muted small">Version {d.rule.version} · from {d.rule.provenance} · {d.rule.because}</p>
              </div>
            </>
          )}

          <h3>What was learned</h3>
          {d.lessons.length === 0 ? (
            <p className="muted small">Nothing recorded against this correction yet.</p>
          ) : (
            <ul className="lesson-list">
              {d.lessons.map((l) => (
                <li key={l.lessonId} className={l.wasNegative ? 'negative' : 'positive'}>
                  <span className={`tag sig ${l.wasNegative ? 'high' : 'ok'}`}>{l.wasNegative ? 'did not work' : 'worked'}</span>
                  <strong>{l.summary}</strong>
                  <p className="muted small">{l.detail}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </aside>
  )
}
